<script setup lang="ts">
const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");
const route = useRoute();

type Capsule = { label: string; to: string };

const props = withDefaults(
  defineProps<{
    items: Capsule[];
    exclude?: string;
    limit?: number;
    heading?: string;
    subtitle?: string;
  }>(),
  {
    exclude: '',
    limit: 9,
    heading: 'Related Capabilities',
    subtitle:
      'Explore more development services and technologies that complement your project.',
  },
);

const visibleCapsules = computed(() => {
  const exclude = props.exclude;
  const filtered = props.items.filter(
    (c) =>
      !c.to.includes(exclude) &&
      !c.label.toLowerCase().includes(exclude.toLowerCase().replace(/-/g, ' ')),
  );

  // Deterministic seed using the route path to ensure consistency across SSR & hydration
  const seed = route.path
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  const shuffled = [...filtered];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor((seed + i) % (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, props.limit);
});
</script>

<template>
  <div class="text-center">
    <h2
      class="text-[28px] lg:text-[48px] font-avant-demi font-semibold leading-tight mx-auto px-4 text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
    >
      {{ heading }}
    </h2>
    <p
      class="text-center mt-4 max-w-[280px] lg:max-w-none mx-auto text-[16px] lg:text-[18px] lg:opacity-70 font-avant tracking-[0.36px]"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subtitle }}
    </p>

    <!-- Capsules -->
    <div class="flex flex-wrap justify-center gap-x-3 gap-y-2 mt-10 px-4">
      <NuxtLink
        v-for="capsule in visibleCapsules"
        :key="capsule.to"
        :to="capsule.to"
        class="flex items-center gap-[7px] px-[14px] py-2 rounded-[50px] border transition-all duration-300 ease-in-out text-center"
        :class="
          isLight
            ? 'border-black/14 text-black/80 hover:border-[#BA55FE] hover:bg-[#BA55FE]/[0.08] hover:text-[#613FAA]'
            : 'border-white/14 text-white/80 hover:border-[#BA55FE] hover:bg-[#BA55FE]/[0.1] hover:text-white'
        "
      >
        {{ capsule.label }}
      </NuxtLink>
    </div>
  </div>
</template>